import { Phone, Mail } from "lucide-react";
import type { CoordonneesComponent } from "../../types";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, resolveImageValue } from "../../utils/imageOptions";

const TITLE_COLOR = "#005CA9";
const BUTTON_BG = "#FFA401";

/** Human label for a contact href: strips the `mailto:`/`tel:` scheme so the
 *  pill button can show the actual address ("contact@bcc.cd") next to its icon. */
function contactLabel(href: string): string {
  return href.replace(/^(mailto:|tel:)/i, "");
}

/** Contact details block renderer with image, phone, email and address. */
export function CoordonneesRenderer({ component }: { component: CoordonneesComponent }) {
  const {
    imageSrc,
    imageAlt,
    topTitle,
    introParagraph,
    phoneHref,
    emailHref,
    secondTitle,
    addressLinkText,
    addressLinkHref,
    addressLines = [],
  } = component.content;

  const { src, options } = resolveImageValue(imageSrc);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const resolvedPhone = resolveLinkHref(phoneHref);
  const resolvedEmail = resolveLinkHref(emailHref);
  const resolvedAddressLink = resolveLinkHref(addressLinkHref);

  return (
    <section>
      <div className="grid min-w-0 grid-cols-1 gap-0 md:grid-cols-2 md:items-stretch">
        <div
          className="relative min-h-0 overflow-hidden md:min-w-0"
          style={{ minHeight: "min(50vmin, 280px)", minWidth: "min(280px, 100%)" }}
        >
          <RuntimeImage
            src={src}
            alt={imageAlt}
            className="absolute inset-0 h-full w-full object-cover"
            style={objectPositionStyle}
            priority
          />
        </div>
        <div className="flex flex-col justify-center p-7 text-left md:p-9 xl:p-10" style={{ backgroundColor: "#EDF7FC" }}>
          <h3
            className="text-xl font-bold leading-tight md:text-[1.35rem] xl:text-2xl"
            style={{ color: TITLE_COLOR, fontFamily: "var(--font-aleo)" }}
          >
            {topTitle}
          </h3>
          {introParagraph ? (
            <p className="mt-2 text-sm xl:text-base leading-6 text-[#0F2849] md:text-base">{introParagraph}</p>
          ) : null}
          {/* Pilules de contact — accent ambre du site (cf. header / partage),
              icône + coordonnée lisible dans le bouton, jamais d'icône seule. */}
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            {resolvedPhone ? (
              <RuntimeLink
                href={resolvedPhone}
                className="inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm xl:text-base font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: BUTTON_BG }}
              >
                <Phone className="h-[18px] w-[18px] shrink-0" aria-hidden />
                {contactLabel(resolvedPhone)}
              </RuntimeLink>
            ) : null}
            {resolvedEmail ? (
              <RuntimeLink
                href={resolvedEmail}
                className="inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm xl:text-base font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: BUTTON_BG }}
              >
                <Mail className="h-[18px] w-[18px] shrink-0" aria-hidden />
                {contactLabel(resolvedEmail)}
              </RuntimeLink>
            ) : null}
          </div>
          {secondTitle ? (
            <h4 className="mt-6 text-[17px] xl:text-[19px] font-semibold md:text-lg" style={{ color: TITLE_COLOR }}>
              {secondTitle}
            </h4>
          ) : null}
          <div className="mt-2 space-y-1 text-sm xl:text-base text-[#0F2849] md:text-base">
            {addressLinkText && resolvedAddressLink ? (
              <RuntimeLink
                href={resolvedAddressLink}
                className="underline"
                style={{ color: TITLE_COLOR }}
              >
                {addressLinkText}
              </RuntimeLink>
            ) : addressLinkText ? (
              <span>{addressLinkText}</span>
            ) : null}
            {addressLines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
